import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Truck,
  Clock,
  XCircle,
  CheckCircle2,
  FileText,
  Download,
  RotateCcw,
  Package,
  ShoppingBag,
  ArrowLeft,
  Tag,
  AlertCircle
} from 'lucide-react';
import { getProductImageUrl, fetchOrdersFromBackend, createStripeCheckoutSessionBackend } from '../services/api';

const UserOrders = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCancelledReturn = searchParams.get('cancelled') === 'true';

  // User Auth Check
  const userJson = localStorage.getItem('user');
  let user = { name: 'Customer' };
  try {
    if (userJson) user = JSON.parse(userJson);
  } catch (e) {}

  // Filter & Selected Order State
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All Orders'); // 'All Orders' | 'Processing' | 'Shipped' | 'Delivered' | 'Cancelled'
  const [payingOrderId, setPayingOrderId] = useState(null);

  const handlePayOrder = async (order, e) => {
    if (e) e.stopPropagation();
    try {
      setPayingOrderId(order.id);
      const res = await createStripeCheckoutSessionBackend(order.id);
      if (res && res.success && res.url) {
        window.location.href = res.url;
      } else {
        alert(res?.message || "Failed to initialize Stripe checkout. Please try again.");
      }
    } catch (err) {
      alert("Error connecting to payment gateway.");
    } finally {
      setPayingOrderId(null);
    }
  };
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Fetch Real Customer Orders from Backend API
  useEffect(() => {
    let isMounted = true;
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const data = await fetchOrdersFromBackend();
        if (data && data.success && isMounted && Array.isArray(data.orders)) {
          const formatted = data.orders.map(o => {
            const d = new Date(o.created_at || Date.now());
            const itemsList = Array.isArray(o.items) ? o.items : [];
            const firstImg = getProductImageUrl(itemsList[0]?.image_url || itemsList[0]?.image || '/uploads/panel_72led.jpg');
            const isUnpaid = o.payment_status !== 'Paid' || (o.order_status || '').toLowerCase() === 'pending payment' || (o.order_status || '').toLowerCase() === 'order not placed';
            
            return {
              id: o.order_number ? o.order_number.replace('#', '') : String(o.id),
              order_number: o.order_number ? o.order_number : `#IR${o.id}`,
              created_at: o.created_at,
              date_formatted: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
              time_formatted: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
              order_status: isUnpaid ? 'Order Not Placed' : (o.order_status || 'Processing'),
              is_unpaid: isUnpaid,
              rawOrder: o,
              delivery_date_formatted: isUnpaid ? 'Awaiting Payment' : '3-5 Business Days',
              status_message: isUnpaid ? 'Payment was not completed. Order is not placed.' : ((o.order_status || '').toLowerCase() === 'delivered' ? 'Your order has been delivered successfully.' : (o.order_status || '').toLowerCase() === 'shipped' ? 'Your order is on the way.' : 'We are preparing your order.'),
              items_count: itemsList.length,
              subtotal: Number(o.subtotal || o.total_amount || 0),
              shipping_fee: Number(o.shipping_fee || 0),
              discount_amount: Number(o.discount_amount || 0),
              coupon_code: o.coupon_code || null,
              tax_amount: Number(o.tax_amount || 0),
              total_amount: Number(o.total_amount || 0),
              shipping_address: o.shipping_address || null,
              payment_method_info: `Paid via ${o.payment_method || 'Card'}`,
              image_url: firstImg,
              items: itemsList.map(it => ({
                name: it.name || 'InfraEdge Panel',
                specs: '660nm + 850nm • Dual Chip',
                quantity: it.quantity || 1,
                price: Number(it.price || 0),
                selected_addons: Array.isArray(it.selected_addons) ? it.selected_addons : [],
                image: getProductImageUrl(it.image_url || it.image || firstImg)
              }))
            };
          });
          setOrders(formatted);
          if (formatted.length > 0) {
            setSelectedOrderId(formatted[0].id);
          }
        } else if (isMounted) {
          setOrders([]);
        }
      } catch (e) {
        if (isMounted) {
          setOrders([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchOrders();
    return () => { isMounted = false; };
  }, []);

  // Filter Orders List
  const filteredOrders = orders.filter(o => {
    if (filterStatus === 'All Orders') return true;
    return o.order_status?.toLowerCase() === filterStatus.toLowerCase();
  });

  const selectedOrder = filteredOrders.find(o => o.id === selectedOrderId) || filteredOrders[0] || null;

  // Helper for Status Pills Styling matching my orders.png 1:1
  const renderStatusPill = (status) => {
    const s = status ? status.toLowerCase() : 'processing';
    let bg = '#dcfce7';
    let color = '#15803d';
    let border = '#bbf7d0';

    if (s === 'order not placed' || s === 'pending payment') {
      bg = '#fff7ed';
      color = '#c2410c';
      border = '#fed7aa';
      return (
        <span
          style={{
            fontSize: '11px',
            fontWeight: 800,
            color: '#c2410c',
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            padding: '2px 10px',
            borderRadius: '9999px',
            display: 'inline-block'
          }}
        >
          Order Not Placed
        </span>
      );
    } else if (s === 'shipped') {
      bg = '#eff6ff';
      color = '#1d4ed8';
      border = '#bfdbfe';
    } else if (s === 'processing') {
      bg = '#fff7ed';
      color = '#c2410c';
      border = '#ffedd5';
    } else if (s === 'cancelled') {
      bg = '#fef2f2';
      color = '#b91c1c';
      border = '#fecaca';
    }

    return (
      <span
        style={{
          fontSize: '11px',
          fontWeight: 800,
          color: color,
          background: bg,
          border: `1px solid ${border}`,
          padding: '2px 10px',
          borderRadius: '9999px',
          display: 'inline-block'
        }}
      >
        {status}
      </span>
    );
  };

  const renderStatusSubtext = (order) => {
    const s = order.order_status?.toLowerCase();
    if (order.is_unpaid || s === 'order not placed' || s === 'pending payment') {
      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: '#c2410c', marginTop: '6px' }}>
          <Clock size={14} color="#ea580c" style={{ marginTop: '2px', flexShrink: 0 }} />
          <div>
            <strong>Payment Incomplete</strong>
            <div style={{ color: '#64748b' }}>{order.status_message}</div>
          </div>
        </div>
      );
    }
    if (s === 'delivered') {
      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: '#475569', marginTop: '6px' }}>
          <Truck size={14} color="#64748b" style={{ marginTop: '2px', flexShrink: 0 }} />
          <div>
            <strong>Delivered on {order.delivery_date_formatted || 'May 20, 2024'}</strong>
            <div style={{ color: '#64748b' }}>{order.status_message}</div>
          </div>
        </div>
      );
    } else if (s === 'shipped') {
      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: '#475569', marginTop: '6px' }}>
          <Truck size={14} color="#1d4ed8" style={{ marginTop: '2px', flexShrink: 0 }} />
          <div>
            <strong>Expected Delivery: {order.delivery_date_formatted || 'May 18, 2024'}</strong>
            <div style={{ color: '#64748b' }}>{order.status_message}</div>
          </div>
        </div>
      );
    } else if (s === 'cancelled') {
      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: '#475569', marginTop: '6px' }}>
          <XCircle size={14} color="#b91c1c" style={{ marginTop: '2px', flexShrink: 0 }} />
          <div>
            <strong>Order Cancelled on {order.delivery_date_formatted || 'Mar 19, 2024'}</strong>
            <div style={{ color: '#64748b' }}>{order.status_message}</div>
          </div>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: '#475569', marginTop: '6px' }}>
        <Clock size={14} color="#c2410c" style={{ marginTop: '2px', flexShrink: 0 }} />
        <div>
          <strong>Order Confirmed</strong>
          <div style={{ color: '#64748b' }}>{order.status_message}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="user-orders-container" style={{ minHeight: '100vh', background: '#fafbfc', padding: '40px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        
        {/* TOP ROW: TITLE & FILTER DROPDOWN MATCHING my orders.png 1:1 */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a', margin: '0 0 4px 0', letterSpacing: '-0.02em' }}>
              My Orders
            </h1>
            <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
              Track, view and manage all your orders.
            </p>
          </div>

          {isCancelledReturn && (
            <div style={{ width: '100%', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '12px', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px', color: '#c2410c', fontSize: '13.5px', fontWeight: 700, margin: '12px 0' }}>
              <AlertCircle size={18} color="#ea580c" style={{ flexShrink: 0 }} />
              <span>Checkout was cancelled or payment was not completed. You can click "Complete Payment" below whenever you are ready.</span>
            </div>
          )}

          {/* CUSTOM FILTER DROPDOWN PILL BUTTON */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: 700,
                color: '#0f172a',
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
              }}
            >
              <Calendar size={15} color="#64748b" />
              <span>{filterStatus}</span>
              <ChevronDown size={14} color="#64748b" style={{ transform: isFilterOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
            </button>

            {isFilterOpen && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '110%',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                  zIndex: 50,
                  width: '180px',
                  overflow: 'hidden',
                  padding: '6px'
                }}
              >
                {['All Orders', 'Processing', 'Shipped', 'Delivered', 'Cancelled'].map((st) => (
                  <button
                    key={st}
                    onClick={() => {
                      setFilterStatus(st);
                      setIsFilterOpen(false);
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 12px',
                      background: filterStatus === st ? '#f8fafc' : 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: filterStatus === st ? 800 : 600,
                      color: filterStatus === st ? '#dc2626' : '#334155',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span>{st}</span>
                    {filterStatus === st && <CheckCircle2 size={13} color="#dc2626" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* IF NO ORDERS: CLEAN TRANSPARENT NO-WHITE-BG EMPTY STATE */}
        {orders.length === 0 ? (
          <div style={{ background: 'transparent', padding: '60px 20px', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <ShoppingBag size={36} color="#dc2626" />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 900, color: '#0f172a', margin: '0 0 8px 0' }}>
              No Orders Placed Yet
            </h2>
            <p style={{ fontSize: '14.5px', color: '#64748b', margin: '0 0 28px 0', lineHeight: 1.5 }}>
              You haven't placed any orders with us yet. Browse our professional red light therapy panels and start your recovery journey today!
            </p>
            <Link
              to="/shop"
              style={{
                background: '#dc2626',
                color: '#ffffff',
                padding: '14px 36px',
                borderRadius: '9999px',
                fontSize: '14px',
                fontWeight: 800,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 6px 18px rgba(220, 38, 38, 0.35)'
              }}
            >
              <span>Explore Red Light Panels</span>
              <span>➔</span>
            </Link>
          </div>
        ) : (
          /* FULL WIDTH ORDERS LIST (NO RIGHT PANEL MODAL/SPLIT) */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '900px', margin: '0 auto' }}>
            {filteredOrders.length === 0 ? (
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '48px', textAlign: 'center' }}>
                <ShoppingBag size={48} color="#cbd5e1" style={{ margin: '0 auto 12px' }} />
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>No orders found</h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 20px' }}>No orders matching "{filterStatus}".</p>
                <Link to="/shop" style={{ background: '#dc2626', color: '#ffffff', padding: '10px 24px', borderRadius: '9999px', fontSize: '13px', fontWeight: 800, textDecoration: 'none' }}>
                  Explore Products
                </Link>
              </div>
            ) : (
              filteredOrders.map((o) => (
                <div
                  key={o.id}
                  onClick={() => navigate(`/orders/${o.id}`)}
                  className="user-orders-card"
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '18px',
                    padding: '20px 24px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '20px'
                  }}
                >
                  {/* Left Product Image Thumbnail */}
                  <div style={{ width: '64px', height: '64px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', flexShrink: 0 }}>
                    <img src={o.image_url} alt={o.order_number} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                  </div>

                  {/* Center Order Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <span style={{ fontSize: '15px', fontWeight: 900, color: '#0f172a' }}>Order {o.order_number}</span>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>• {o.date_formatted}</span>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>• {o.items_count} {o.items_count === 1 ? 'Item' : 'Items'}</span>
                      {renderStatusPill(o.order_status)}
                    </div>

                    {/* Add-ons Badge Summary */}
                    {o.items.some(it => Array.isArray(it.selected_addons) && it.selected_addons.length > 0) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '3px 0 5px 0' }}>
                        {o.items.flatMap(it => it.selected_addons || []).map((addon, aIdx) => (
                          <span key={aIdx} style={{ fontSize: '10px', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fee2e2', padding: '1px 6px', borderRadius: '4px' }}>
                            + {addon.addon_name || addon.name} (+£{Number(addon.addon_price || addon.price || 0).toFixed(2)})
                          </span>
                        ))}
                      </div>
                    )}

                    {renderStatusSubtext(o)}
                  </div>

                  {/* Right Price & View Details Action */}
                  <div className="user-orders-card-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a' }}>
                        £{o.total_amount.toFixed(2)}
                      </span>
                      <ChevronRight size={16} color="#94a3b8" />
                    </div>

                    {o.is_unpaid ? (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          disabled={payingOrderId === o.id}
                          onClick={(e) => handlePayOrder(o.rawOrder || o, e)}
                          style={{
                            background: '#dc2626',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '6px 14px',
                            fontSize: '12px',
                            fontWeight: 800,
                            cursor: payingOrderId === o.id ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)'
                          }}
                        >
                          {payingOrderId === o.id ? 'Connecting...' : 'Complete Payment'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/orders/${o.id}`);
                          }}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #cbd5e1',
                            borderRadius: '8px',
                            padding: '6px 14px',
                            fontSize: '12px',
                            fontWeight: 700,
                            color: '#334155',
                            cursor: 'pointer'
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/orders/${o.id}`);
                        }}
                        style={{
                          background: '#ffffff',
                          border: '1px solid #cbd5e1',
                          borderRadius: '8px',
                          padding: '6px 14px',
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#334155',
                          cursor: 'pointer'
                        }}
                      >
                        View Details
                      </button>
                    )}
                  </div>

                </div>
              ))
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default UserOrders;
